import { z } from 'zod';
import { COUNTRY_ISO_CODES, getCountryName } from '@/lib/country-list';

// Optional details preserve the existing pricing form's email/message contract.
const contactMessageSchema = z.object({
  email: z.string().trim().email().max(320),
  message: z.string().trim().min(1).max(10_000),
  name: z.string().trim().min(1).max(120).optional(),
  company: z.string().trim().max(160).optional(),
  subject: z.string().trim().min(1).max(200).optional(),
  sessionId: z.string().regex(/^[A-Za-z0-9_-]{16,160}$/),
});

export const ORGANIZATION_TYPES = [
  'brand',
  'retailer',
  'manufacturer',
  'agency',
  'other',
] as const;

export const businessContactSchema = z.object({
  formType: z.literal('business'),
  firstName: z.string().trim().min(1).max(120),
  lastName: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(320),
  company: z.string().trim().min(1).max(160),
  jobTitle: z.string().trim().min(1).max(160),
  phone: z.string().trim().max(50),
  organizationType: z.enum(ORGANIZATION_TYPES),
  country: z
    .string()
    .refine((value) => COUNTRY_ISO_CODES.some((iso) => iso === value)),
  marketingConsent: z.boolean(),
  sessionId: z.string().regex(/^[A-Za-z0-9_-]{16,160}$/),
});

export type BusinessContactFields = Omit<
  z.infer<typeof businessContactSchema>,
  'sessionId'
>;

export function parseContactMessage(input: unknown) {
  if (input && typeof input === 'object' && 'formType' in input) {
    const parsed = businessContactSchema.safeParse(input);
    if (!parsed.success) return null;
    const fields = parsed.data;
    return {
      email: fields.email,
      sessionId: fields.sessionId,
      message: [
        `First name: ${fields.firstName}`,
        `Last name: ${fields.lastName}`,
        `Business email address: ${fields.email}`,
        `Company name: ${fields.company}`,
        `Job title: ${fields.jobTitle}`,
        `Phone number: ${fields.phone || 'Not provided'}`,
        `Organization type: ${fields.organizationType}`,
        `Country: ${getCountryName(fields.country, 'en')} (${fields.country})`,
        `Marketing consent: ${fields.marketingConsent ? 'Yes' : 'No'}`,
      ].join('\n'),
    };
  }
  const parsed = contactMessageSchema.safeParse(input);
  if (!parsed.success) return null;
  const { name, company, subject, message, ...rest } = parsed.data;
  const details = [
    name && `Name: ${name}`,
    company && `Company: ${company}`,
    subject && `Subject: ${subject}`,
  ].filter(Boolean);
  const formattedMessage = details.length
    ? `${details.join('\n')}\n\n${message}`
    : message;
  if (formattedMessage.length > 10_000) return null;
  return {
    ...rest,
    message: formattedMessage,
  };
}
